import { useState, type FormEvent, type ReactNode } from 'react';
import { errorMessage } from '../../api/client';
import type {
  DomainCheck,
  MailboxResult,
  RecordMismatch,
  RequiredRecord,
  WhitelistResult,
} from '../../api/types';
import { Alert, Badge, Button, PasswordField, TextField, type Tone } from '../ui';
import { EmptyState } from '../states';

/**
 * What a domain still needs, the mailboxes on it, and the sender allow-list.
 *
 * Balsam does not write DNS: these records are added by hand in Cloudflare,
 * and the domain itself is added by hand in the MXroute panel. So the DNS part
 * is a to-do list you copy from, not a set of buttons that applies anything.
 * The two things that *are* buttons — a mailbox, an allow-list entry — are the
 * additive writes MXroute allows, and the forms here take the call to make as
 * a prop, so the same form serves the tenant route and the staff one.
 */

type Notify = (text: string, kind?: 'success' | 'info' | 'error') => void;

/** Ready, or the first thing standing in the way. */
export function checkSummary(check: DomainCheck): { tone: Tone; label: string; hint: string } {
  if (!check.onMxroute) {
    return {
      tone: 'warn',
      label: 'Not on the mail server yet',
      hint: 'The domain has to be added in the MXroute panel before mail can be delivered. Balsam does that step.',
    };
  }
  if (check.ready) {
    return { tone: 'ok', label: 'Ready', hint: 'Every record this domain needs is in place.' };
  }
  const outstanding = check.missing.length + check.wrong.length;
  return {
    tone: 'warn',
    label: `${outstanding} record${outstanding === 1 ? '' : 's'} to fix`,
    hint: 'Add the records below in your DNS, then check again.',
  };
}

/** The DNS to-do list: what is missing, and what is there but wrong. */
export function DnsTodo({ check }: { check: DomainCheck }) {
  if (check.missing.length === 0 && check.wrong.length === 0) return null;
  return (
    <div className="dns-todo">
      {check.missing.length > 0 ? (
        <>
          <h4>Add these records</h4>
          <RecordTable records={check.missing} />
        </>
      ) : null}
      {check.wrong.length > 0 ? (
        <>
          <h4>Change these records</h4>
          <table className="table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Name</th>
                <th>Change to</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {check.wrong.map((m: RecordMismatch) => (
                <tr key={`${m.required.type} ${m.required.name}`}>
                  <td>
                    <Badge tone="neutral">{m.required.type}</Badge>
                  </td>
                  <td>
                    <code>{m.required.name}</code>
                  </td>
                  <td>
                    <code className="wrap">{m.suggested}</code>
                  </td>
                  <td className="muted">{m.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      ) : null}
    </div>
  );
}

function RecordTable({ records }: { records: RequiredRecord[] }) {
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Type</th>
          <th>Name</th>
          <th>Value</th>
          <th>Priority</th>
        </tr>
      </thead>
      <tbody>
        {records.map((r) => (
          <tr key={`${r.type} ${r.name} ${r.content}`}>
            <td>
              <Badge tone="neutral">{r.type}</Badge>
            </td>
            <td>
              <code>{r.name}</code>
            </td>
            <td>
              <code className="wrap">{r.content}</code>
            </td>
            <td>{r.priority ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Things worth knowing that never block anything. */
export function Advisories({ check }: { check: DomainCheck }) {
  if (check.advisories.length === 0) return null;
  return (
    <ul className="advisories">
      {check.advisories.map((a) => (
        <li key={a.code} className="muted">
          {a.message}
        </li>
      ))}
    </ul>
  );
}

/**
 * The mailboxes on the domain, straight from MXroute — the hub keeps no copy.
 * `children` is the slot under the list: a CreateMailboxForm for a caller who
 * may create one, a note for one who may not.
 */
export function MailboxesPanel({ check, children }: { check: DomainCheck; children?: ReactNode }) {
  return (
    <section className="mailboxes">
      <h4>Mailboxes</h4>
      {check.mailboxes.length === 0 ? (
        <EmptyState title="No mailboxes yet" body="Create the first one below." />
      ) : (
        <ul className="mailbox-list">
          {check.mailboxes.map((local) => (
            <li key={local}>
              <code>
                {local}@{check.domain}
              </code>
            </li>
          ))}
        </ul>
      )}
      {children}
    </section>
  );
}

/**
 * Create one mailbox.
 *
 * The password is chosen here and sent once; it is never stored by the hub and
 * never comes back. If the mailbox already exists the call succeeds and the
 * existing password is left alone — nothing here resets one. `onCreate` is
 * whichever route the page uses, so the form knows nothing about tenants.
 */
export function CreateMailboxForm({
  domain,
  onCreate,
  onChanged,
}: {
  domain: string;
  onCreate: (input: { localPart: string; password: string }) => Promise<MailboxResult>;
  onChanged: Notify;
}) {
  const [localPart, setLocalPart] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const result = await onCreate({ localPart: localPart.trim().toLowerCase(), password });
      setLocalPart('');
      setPassword('');
      onChanged(
        result.created
          ? `Created ${result.localPart}@${result.domain}.`
          : `${result.localPart}@${result.domain} already existed; its password was not changed.`,
        result.created ? 'success' : 'info',
      );
    } catch (failure) {
      setError(failure);
    } finally {
      setPending(false);
    }
  };

  return (
    <form className="stack" onSubmit={(event) => void submit(event)}>
      {error ? <Alert kind="error">{errorMessage(error)}</Alert> : null}
      <TextField
        label="Mailbox name"
        value={localPart}
        onChange={(e) => setLocalPart(e.target.value)}
        hint={`The part before the @ — ${localPart.trim().toLowerCase() || 'hello'}@${domain}`}
        required
      />
      <PasswordField
        label="Password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        hint="At least 8 characters with an uppercase letter, a lowercase letter and a digit. Balsam never stores it."
        required
      />
      <Button type="submit" loading={pending} disabled={!localPart.trim() || !password}>
        Create mailbox
      </Button>
    </form>
  );
}

/**
 * The sender allow-list: senders who skip MXroute's spam filter.
 *
 * **Account-wide.** MXroute shares one list across every domain on the
 * account, so an entry added "for" this domain applies to all of them. That is
 * why the tenant portal shows it read-only and only staff get `onAdd` /
 * `onRemove`. An entry is an address or a pattern such as `*@partner.com`.
 */
export function WhitelistPanel({
  whitelist,
  onAdd,
  onRemove,
  onChanged,
}: {
  whitelist: readonly string[];
  onAdd?: (entries: string[]) => Promise<WhitelistResult>;
  onRemove?: (entries: string[]) => Promise<WhitelistResult>;
  onChanged: Notify;
}) {
  const [draft, setDraft] = useState('');
  /** `add`, or the entry being removed, while a call is in flight. */
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  const entries = draft
    .split(/[,\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);

  const run = async (label: string, call: () => Promise<WhitelistResult>, said: string) => {
    setBusy(label);
    setError(null);
    try {
      const result = await call();
      if (label === 'add') setDraft('');
      const unchanged =
        result.unchanged.length > 0 ? ` ${result.unchanged.join(', ')} unchanged.` : '';
      onChanged(`${said}${unchanged}`, result.changed.length > 0 ? 'success' : 'info');
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="whitelist">
      <h4>Sender allow-list</h4>
      <p className="muted">
        Senders here skip the spam filter — for every domain on the mail account, not only this one.
      </p>
      {error ? <Alert kind="error">{errorMessage(error)}</Alert> : null}
      {whitelist.length === 0 ? (
        <p className="muted">Nobody is on the list.</p>
      ) : (
        <ul className="mailbox-list">
          {whitelist.map((entry) => (
            <li key={entry}>
              <code>{entry}</code>{' '}
              {onRemove ? (
                <Button
                  variant="secondary"
                  aria-label={`Remove ${entry}`}
                  loading={busy === entry}
                  disabled={busy !== null}
                  onClick={() =>
                    void run(
                      entry,
                      () => onRemove([entry]),
                      `Removed ${entry} from the allow-list.`,
                    )
                  }
                >
                  Remove
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {onAdd ? (
        <form
          className="stack"
          onSubmit={(event) => {
            event.preventDefault();
            if (entries.length === 0) return;
            void run(
              'add',
              () => onAdd(entries),
              `Allowed ${entries.length} sender${entries.length === 1 ? '' : 's'}.`,
            );
          }}
        >
          <TextField
            label="Senders to allow"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            hint="Addresses or patterns like *@partner.com, separated by commas or spaces."
          />
          <Button type="submit" loading={busy === 'add'} disabled={entries.length === 0}>
            Allow senders
          </Button>
        </form>
      ) : null}
    </section>
  );
}
